import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import { getUserById, getUserSchedules, deleteUserSchedule } from '../../services/users/usersService';
import { ScheduleActionDto, UserDto } from '../../services/users/types';
import ConfirmDialog from '../../components/UI/ConfirmDialog';

// Week starts on Sunday (0) through Saturday (6)
const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const UserSchedules: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserDto | null>(null);
  const [schedules, setSchedules] = useState<ScheduleActionDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduleActionDto | null>(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [calendarDays, setCalendarDays] = useState<Date[]>([]);
  // const [viewMode, setViewMode] = useState<'month' | 'week' | 'day'>('month');
  
  // Função para gerar os dias do mês atual para exibição no calendário
  const generateCalendarDays = useCallback(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // Primeiro dia do mês
    const firstDay = new Date(year, month, 1);
    
    // Último dia do mês
    const lastDay = new Date(year, month + 1, 0);
    
    // Dia da semana do primeiro dia (0 = Dom, 6 = Sáb)
    const firstDayOfWeek = firstDay.getDay();
    
    // Array para armazenar todos os dias que serão exibidos no calendário
    const days: Date[] = [];
    
    // Adicionar dias do mês anterior para completar a primeira semana
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      days.push(new Date(year, month, -i));
    }
    
    // Adicionar todos os dias do mês atual
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push(new Date(year, month, day));
    }
    
    // Adicionar dias do próximo mês para completar a última semana
    const remainingDays = 7 - (days.length % 7);
    if (remainingDays < 7) {
      for (let i = 1; i <= remainingDays; i++) {
        days.push(new Date(year, month + 1, i));
      }
    }
    
    setCalendarDays(days);
  }, [currentDate]);
  
  // Buscar dados do usuário e seus agendamentos
  const fetchData = useCallback(async (userId: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Buscar dados do usuário e agendamentos em paralelo
      const [userData, schedulesData] = await Promise.all([
        getUserById(auth.user || null, userId),
        getUserSchedules(auth.user || null, userId, 1, 100) // Buscar um número razoável de agendamentos
      ]);
      
      setUser(userData);
      setSchedules(schedulesData.content || []);
      
      // Selecionar a data atual por padrão
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      setSelectedDate(today);
      
    } catch (err) {
      console.error('Error fetching schedules data:', err);
      setError('Failed to load schedules. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [auth.user]);
  
  // Buscar dados do usuário e agendamentos
  useEffect(() => {
    if (id) {
      fetchData(id);
    }
  }, [id, fetchData]);
  
  // Gerar dias do calendário quando a data atual muda
  useEffect(() => {
    generateCalendarDays();
  }, [currentDate, generateCalendarDays]);
  
  // Navegar para o mês anterior
  const goToPreviousMonth = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };
  
  // Navegar para o próximo mês
  const goToNextMonth = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };
  
  // Selecionar um dia no calendário
  const handleSelectDate = (date: Date) => {
    setSelectedDate(date);
  };
  
  // Verificar se uma data tem agendamentos
  const hasSchedulesOnDate = (date: Date): boolean => {
    const dateStr = date.toISOString().split('T')[0]; // Formato YYYY-MM-DD
    
    return schedules.some(schedule => {
      // Verificar se a data está dentro do período de agendamento
      const startsIn = new Date(schedule.starts_in);
      // const endsIn = new Date(schedule.ends_in);
      
      // Para eventos recorrentes, verificar o padrão de repetição
      if (schedule.scheduled_type === 'RECURRING') {
        if (date >= startsIn) {
          // Se tem data final de recorrência, verificar se a data está antes dela
          if (schedule.recurring_until) {
            const recurringUntil = new Date(schedule.recurring_until);
            if (date > recurringUntil) return false;
          }
          
          // Verificar o padrão de repetição
          switch (schedule.repetition_type) {
            case 'DAILY':
              return true; // Ocorre todos os dias
            case 'WEEKLY':
              return date.getDay() === startsIn.getDay(); // Mesmo dia da semana
            case 'MONTHLY':
              return date.getDate() === startsIn.getDate(); // Mesmo dia do mês
            case 'YEARLY':
              return date.getMonth() === startsIn.getMonth() && 
                     date.getDate() === startsIn.getDate(); // Mesma data anual
            default:
              return false;
          }
        }
        return false;
      }
      
      // Para eventos não recorrentes (uma vez), verificar se a data é exatamente a data de início
      const dateOnly = new Date(dateStr);
      const startDateOnly = new Date(startsIn.toISOString().split('T')[0]);
      
      return dateOnly.getTime() === startDateOnly.getTime();
    });
  };
  
  // Obter agendamentos para uma data específica
  const getSchedulesForDate = (date: Date): ScheduleActionDto[] => {
    const dateStr = date.toISOString().split('T')[0]; // Formato YYYY-MM-DD
    
    return schedules.filter(schedule => {
      // Verificar se a data está dentro do período de agendamento
      const startsIn = new Date(schedule.starts_in);
      // const endsIn = new Date(schedule.ends_in);
      
      // Para eventos recorrentes, verificar o padrão de repetição
      if (schedule.scheduled_type === 'RECURRING') {
        if (date >= startsIn) {
          // Se tem data final de recorrência, verificar se a data está antes dela
          if (schedule.recurring_until) {
            const recurringUntil = new Date(schedule.recurring_until);
            if (date > recurringUntil) return false;
          }
          
          // Verificar o padrão de repetição
          switch (schedule.repetition_type) {
            case 'DAILY':
              return true; // Ocorre todos os dias
            case 'WEEKLY':
              return date.getDay() === startsIn.getDay(); // Mesmo dia da semana
            case 'MONTHLY':
              return date.getDate() === startsIn.getDate(); // Mesmo dia do mês
            case 'YEARLY':
              return date.getMonth() === startsIn.getMonth() && 
                     date.getDate() === startsIn.getDate(); // Mesma data anual
            default:
              return false;
          }
        }
        return false;
      }
      
      // Para eventos não recorrentes (uma vez), verificar se a data é exatamente a data de início
      const dateOnly = new Date(dateStr);
      const startDateOnly = new Date(startsIn.toISOString().split('T')[0]);
      
      return dateOnly.getTime() === startDateOnly.getTime();
    });
  };
  
  // Manipular a confirmação de exclusão de agendamento
  const handleDeleteClick = (schedule: ScheduleActionDto) => {
    setSelectedSchedule(schedule);
    setShowConfirmDelete(true);
  };
  
  // Confirmar a exclusão do agendamento
  const handleConfirmDelete = async () => {
    if (!id || !selectedSchedule?.id) return;
    
    setIsDeleting(true);
    
    try {
      await deleteUserSchedule(auth.user || null, id, selectedSchedule.id);
      
      // Atualizar a lista de agendamentos após exclusão
      setSchedules(prev => prev.filter(s => s.id !== selectedSchedule.id));
      setShowConfirmDelete(false);
      setSelectedSchedule(null);
    } catch (err) {
      console.error('Error deleting schedule:', err);
      setError('Failed to delete schedule. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Formatar data para exibição
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Format schedule type description
  const getScheduleTypeDescription = (schedule: ScheduleActionDto): string => {
    if (schedule.scheduled_type === 'ONCE') {
      return 'Once';
    }
    
    if (schedule.scheduled_type === 'RECURRING') {
      switch (schedule.repetition_type) {
        case 'DAILY': return 'Daily';
        case 'WEEKLY': return 'Weekly';
        case 'MONTHLY': return 'Monthly';
        case 'YEARLY': return 'Yearly';
        default: return 'Recurring';
      }
    }
    
    return 'Unknown';
  };
  
  return (
    <div>
      <PageHeader
        title={user ? `Schedules for ${user.first_name || user.username}` : "User Schedules"}
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Users", to: "/users" },
          { label: user?.first_name && user?.last_name 
            ? `${user.first_name} ${user.last_name}` 
            : user?.username || 'User', to: `/users/${id}` },
          { label: "Schedules" }
        ]}
        actions={
          <Button
            variant="secondary"
            onClick={() => navigate(`/users/${id}`)}
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Back to User
          </Button>
        }
      />
      
      <div className="mb-6">
        <Link to={`/users/${id}/schedules/create`}>
          <Button variant="primary">
            <i className="fas fa-plus mr-2"></i>
            Add Schedule
          </Button>
        </Link>
      </div>
      
      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4">
          {error}
          <Button 
            variant="ghost" 
            size="sm" 
            className="ml-2"
            onClick={() => id && fetchData(id)}
          >
            Retry
          </Button>
        </div>
      )}
      
      {isLoading ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <Card className="md:col-span-2">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">
                    {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </h2>
                  <div className="flex space-x-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={goToPreviousMonth}
                    >
                      <i className="fas fa-chevron-left"></i>
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setCurrentDate(new Date())}
                    >
                      Today
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={goToNextMonth}
                    >
                      <i className="fas fa-chevron-right"></i>
                    </Button>
                  </div>
                </div>
                
                {/* Calendar header with days of week */}
                <div className="grid grid-cols-7 gap-1 mb-1">
                  {DAYS_OF_WEEK.map(day => (
                    <div 
                      key={day} 
                      className="text-center py-2 font-medium text-sm text-gray-600"
                    >
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Calendar grid */}
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map((day, index) => {
                    const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                    const isToday = day.toDateString() === new Date().toDateString();
                    const isSelected = selectedDate && day.toDateString() === selectedDate.toDateString();
                    const hasSchedules = hasSchedulesOnDate(day);
                    
                    return (
                      <div 
                        key={index}
                        onClick={() => handleSelectDate(day)}
                        className={`
                          h-20 p-1 border rounded-md cursor-pointer transition-colors
                          ${isCurrentMonth ? 'bg-white' : 'bg-gray-50 text-gray-400'}
                          ${isToday ? 'border-blue-500' : 'border-gray-200'}
                          ${isSelected ? 'ring-2 ring-blue-500' : ''}
                          hover:bg-gray-50
                        `}
                      >
                        <div className="flex justify-between">
                          <span className={`
                            text-sm p-1 rounded-full w-6 h-6 flex items-center justify-center
                            ${isToday ? 'bg-blue-500 text-white' : ''}
                          `}>
                            {day.getDate()}
                          </span>
                          
                          {hasSchedules && (
                            <span className="inline-block w-2 h-2 rounded-full bg-blue-500"></span>
                          )}
                        </div>
                        
                        {/* Show first 2 schedule titles */}
                        {hasSchedules && (
                          <div className="mt-1 overflow-hidden text-xs">
                            {getSchedulesForDate(day).slice(0, 2).map((schedule, idx) => (
                              <div 
                                key={idx} 
                                className="truncate text-xs p-0.5 my-0.5 rounded bg-blue-100 text-blue-800"
                                title={schedule.parameters.name}
                              >
                                {schedule.parameters.name}
                              </div>
                            ))}
                            
                            {/* "More schedules" indicator */}
                            {getSchedulesForDate(day).length > 2 && (
                              <div className="text-xs text-gray-500 pl-1">
                                +{getSchedulesForDate(day).length - 2} more
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>
            
            {/* Side panel with selected day details */}
            <Card>
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-4">
                  {selectedDate ? (
                    <>
                      {MONTHS[selectedDate.getMonth()]} {selectedDate.getDate()}
                      <span className="text-sm text-gray-500 ml-2">
                        {DAYS_OF_WEEK[selectedDate.getDay()]}
                      </span>
                    </>
                  ) : (
                    'Select a date'
                  )}
                </h2>
                
                {selectedDate && (
                  <>
                    {getSchedulesForDate(selectedDate).length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <p>No schedules for this date.</p>
                        <Link to={`/users/${id}/schedules/create`} className="mt-4 inline-block">
                          <Button variant="primary" size="sm">
                            Add Schedule
                          </Button>
                        </Link>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {getSchedulesForDate(selectedDate).map((schedule) => (
                          <div 
                            key={schedule.id} 
                            className="p-3 border border-gray-200 rounded-md hover:bg-gray-50"
                          >
                            <div className="flex justify-between items-start">
                              <h3 className="font-medium">
                                {schedule.parameters.name}
                              </h3>
                              <div className="flex space-x-1">
                                <Button 
                                  variant="ghost" 
                                  size="xs"
                                  onClick={() => navigate(`/users/${id}/schedules/${schedule.id}/edit`)}
                                >
                                  <i className="fas fa-edit"></i>
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="xs"
                                  onClick={() => handleDeleteClick(schedule)}
                                >
                                  <i className="fas fa-trash-alt"></i>
                                </Button>
                              </div>
                            </div>
                            
                            <p className="text-sm text-gray-600 mt-1">
                              {schedule.parameters.description || 'No description'}
                            </p>
                            
                            <div className="mt-2 text-xs text-gray-500 space-y-1">
                              <div>
                                Time: {new Date(schedule.starts_in).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                              </div>
                              <div>
                                {getScheduleTypeDescription(schedule)}
                              </div>
                              {schedule.scheduled_type === 'RECURRING' && schedule.recurring_until && (
                                <div>
                                  Until: {new Date(schedule.recurring_until).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                            
                            {schedule.reminders && schedule.reminders.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-gray-100">
                                <p className="text-xs font-medium text-gray-500">Reminders:</p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {schedule.reminders.map((reminder, idx) => (
                                    <span 
                                      key={idx}
                                      className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800"
                                    >
                                      {reminder.value} {reminder.interval.toLowerCase()}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </Card>
          </div>
          
          {/* All schedules list */}
          <Card>
            {schedules.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No schedules for this user.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Start</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {schedules.map((schedule) => (
                      <tr key={schedule.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-medium text-gray-900">{schedule.parameters.name}</div>
                          <div className="text-sm text-gray-500">{schedule.parameters.description}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(schedule.starts_in)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {getScheduleTypeDescription(schedule)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            schedule.cancelled_at 
                              ? 'bg-red-100 text-red-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {schedule.cancelled_at ? 'Cancelled' : 'Active'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <Button 
                              variant="ghost" 
                              size="xs"
                              onClick={() => navigate(`/users/${id}/schedules/${schedule.id}/edit`)}
                            >
                              <i className="fas fa-edit"></i>
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="xs"
                              onClick={() => handleDeleteClick(schedule)}
                            >
                              <i className="fas fa-trash-alt"></i>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
      
      {/* Confirm dialog for deletion */}
      <ConfirmDialog
        isOpen={showConfirmDelete}
        title="Delete Schedule"
        message={`Are you sure you want to delete the schedule "${selectedSchedule?.parameters.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setShowConfirmDelete(false);
          setSelectedSchedule(null);
        }}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default UserSchedules;